export const COVER_IMAGE_MODEL = "gpt-image-2";
export const DEFAULT_IMAGE_CREDIT_COST = 30;
export const COVER_IMAGE_PROMPT_MAX_LENGTH = 10_000;

export type CoverImageType = "youtube" | "podcast";
export type CoverImageAspectRatio = "landscape" | "portrait" | "square";
export type CoverImageContentType =
  | "image/png"
  | "image/jpeg"
  | "image/webp";
export type GrsaiFailureCode =
  | "PROVIDER_CREDITS_EXHAUSTED"
  | "PROVIDER_AUTH_FAILED"
  | "PROVIDER_RATE_LIMITED"
  | "PROVIDER_REQUEST_REJECTED"
  | "PROVIDER_UNAVAILABLE"
  | "PROVIDER_REQUEST_FAILED";

const DRAW_SIZES: Record<CoverImageAspectRatio, string> = {
  landscape: "1672x941",
  portrait: "941x1672",
  square: "1024x1024",
};

const TYPE_GUIDANCE: Record<CoverImageType, string> = {
  youtube:
    "The image should feel immediate, high-energy, and legible at YouTube feed size.",
  podcast:
    "The image should feel distinctive, premium, and recognizable as podcast artwork at small sizes.",
};

const COMPOSITION_GUIDANCE: Record<CoverImageAspectRatio, string> = {
  landscape:
    "Use a cinematic 16:9 landscape composition with the main subject on the right and clean negative space on the left for a bold title.",
  portrait:
    "Use a cinematic 9:16 vertical composition. Keep the main subject in the upper-middle area, leave clean title space in the lower-middle area, and avoid important details near the right edge or bottom where video controls may appear.",
  square:
    "Use a balanced square composition with one unmistakable focal subject and clean space in the upper half for a show title.",
};

export function getCoverDrawSize(aspectRatio: CoverImageAspectRatio) {
  return DRAW_SIZES[aspectRatio];
}

export function buildCoverImagePrompt(
  prompt: string,
  coverType: CoverImageType,
  aspectRatio: CoverImageAspectRatio,
) {
  const cleanedPrompt = prompt.trim().replace(/\s+/g, " ");
  return [
    "Create a high quality cover background based on this creative direction:",
    cleanedPrompt,
    TYPE_GUIDANCE[coverType],
    COMPOSITION_GUIDANCE[aspectRatio],
    "Editorial quality, strong visual hierarchy, clear lighting, and high contrast.",
    "Do not include text, letters, captions, logos, UI, borders, signatures, or watermarks. The user will add typography separately.",
  ].join(" ");
}

export function createCoverImagePayload(
  prompt: string,
  coverType: CoverImageType,
  aspectRatio: CoverImageAspectRatio,
) {
  return {
    model: COVER_IMAGE_MODEL,
    prompt: buildCoverImagePrompt(prompt, coverType, aspectRatio),
    aspectRatio: getCoverDrawSize(aspectRatio),
    variants: 1,
    urls: [] as string[],
    shutProgress: false,
  };
}

export function parseGrsaiPayload(text: string): unknown {
  const normalized = text.trim();
  if (!normalized) {
    throw new Error("Empty response body from GRSAI API");
  }

  try {
    return JSON.parse(normalized);
  } catch {
    const events = normalized
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice(5).trim())
      .filter((payload) => payload && payload !== "[DONE]");

    for (let index = events.length - 1; index >= 0; index -= 1) {
      try {
        return JSON.parse(events[index]);
      } catch {
        // Keep scanning earlier complete SSE messages.
      }
    }

    const jsonMatch = normalized.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        return JSON.parse(jsonMatch[0]);
      } catch {
        // Fall through to the final parse error.
      }
    }
  }

  throw new Error(
    `Unable to parse GRSAI API response: ${normalized.slice(0, 300)}`,
  );
}

export function extractGrsaiTaskId(payload: any) {
  return String(
    payload?.data?.id ||
      payload?.id ||
      payload?.data?.task_id ||
      payload?.task_id ||
      "",
  ).trim();
}

export function extractGrsaiImageUrl(payload: any) {
  const data =
    payload?.data && typeof payload.data === "object" ? payload.data : payload;
  return String(data?.results?.[0]?.url || data?.url || "").trim();
}

export function normalizeGrsaiResult(payload: any) {
  return payload?.data && typeof payload.data === "object"
    ? payload.data
    : payload || {};
}

export function normalizeGrsaiTaskState(payload: any) {
  const result = normalizeGrsaiResult(payload);
  const providerStatus = String(result?.status || "").toLowerCase();
  const imageUrl = extractGrsaiImageUrl(payload);
  const progress = Number(result?.progress);

  if (providerStatus === "failed") {
    return {
      status: "failed" as const,
      progress: Number.isFinite(progress) ? progress : 0,
      error: String(
        result?.failure_reason ||
          result?.error ||
          payload?.msg ||
          "GRSAI image task failed",
      ).slice(0, 500),
    };
  }

  if (
    imageUrl &&
    (providerStatus === "succeeded" ||
      providerStatus === "success" ||
      progress >= 100)
  ) {
    return {
      status: "succeeded" as const,
      progress: 100,
      imageUrl,
    };
  }

  return {
    status: "processing" as const,
    progress: Number.isFinite(progress)
      ? Math.max(0, Math.min(99, progress))
      : 0,
  };
}

export function classifyGrsaiFailure(
  status: number,
  message: string,
): GrsaiFailureCode {
  const normalized = message.toLowerCase();

  if (
    status === 402 ||
    normalized.includes("insufficient credits") ||
    normalized.includes("insufficient balance")
  ) {
    return "PROVIDER_CREDITS_EXHAUSTED";
  }

  if (
    status === 401 ||
    status === 403 ||
    normalized.includes("invalid api key") ||
    normalized.includes("unauthorized")
  ) {
    return "PROVIDER_AUTH_FAILED";
  }

  if (status === 429 || normalized.includes("rate limit")) {
    return "PROVIDER_RATE_LIMITED";
  }

  if ([400, 404, 409, 422].includes(status)) {
    return "PROVIDER_REQUEST_REJECTED";
  }

  if (status >= 500) {
    return "PROVIDER_UNAVAILABLE";
  }

  return "PROVIDER_REQUEST_FAILED";
}

export function isTimeoutLikeError(error: unknown) {
  const candidate = error as { message?: unknown; name?: unknown } | null;
  const name = String(candidate?.name || "").toLowerCase();
  const message = String(candidate?.message || "").toLowerCase();

  return (
    name === "aborterror" ||
    name === "timeouterror" ||
    message.includes("timeout") ||
    message.includes("timed out")
  );
}

export function detectCoverImageContentType(
  bytes: Uint8Array,
): CoverImageContentType | null {
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  ) {
    return "image/png";
  }

  if (
    bytes.length >= 3 &&
    bytes[0] === 0xff &&
    bytes[1] === 0xd8 &&
    bytes[2] === 0xff
  ) {
    return "image/jpeg";
  }

  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return "image/webp";
  }

  return null;
}
